import { APR_LABELS } from '@pickleballinc/lib/product-labels';
import { Application } from '@pickleballinc/lib/types';

import { IS_LOCALHOST } from '@/utils/helpers/constants';

/**
 * Apex domains per public site. This app serves three of them and each has its own Freestar
 * publisher, so the site has to be derived from the request host — unlike the tournament, clubs
 * and team-leagues apps, which own a single domain and hardcode their `application`.
 */
const APP_DOMAINS: [Application, string[]][] = [
  ['pickleball', ['pickleball.com', 'pickleballdev.net', 'pickleballtest.net']],
  ['leagues', ['pickleballleagues.com', 'pickleballleaguesdev.net', 'pickleballleaguestest.net']],
  ['apr-rankings', [APR_LABELS.domainProd, APR_LABELS.domainDev, APR_LABELS.domainTest]],
];

/**
 * Matches an apex domain or any subdomain of it, so branch previews
 * (`pw-7719.pickleballdev.net`) resolve the same as `pickleballdev.net`. Same rule as `hostIs`
 * in the middleware.
 *
 * This used to compare for exact equality, which meant every preview deploy resolved to
 * `undefined` and the root layout rendered no Freestar header scripts at all — ads still appeared
 * on preview because the Freestar adslot component loads pubfig itself on mount, which made the
 * gap easy to miss.
 */
const hostMatches = (host: string, apexDomains: string[]): boolean =>
  apexDomains.some((domain) => host === domain || host.endsWith(`.${domain}`));

export const getAppFromHostname = (hostname?: string | null): Application | undefined => {
  if (IS_LOCALHOST) return 'pickleball';
  if (!hostname) return undefined;

  // `host` carries a port in some environments; the domain list never does.
  const host = hostname.split(':')[0]?.trim().toLowerCase();
  if (!host) return undefined;

  return APP_DOMAINS.find(([, domains]) => hostMatches(host, domains))?.[0];
};
